import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Box, FormControlLabel, Switch, Typography } from '@mui/material'; // Added Box import
import { useRecoilValue, useResetRecoilState } from 'recoil';
import {
  entityDetailsStateAtomFamily,
  flowObjAtom,
  flowValidationAtom,
} from '../../../../Services/_states';
import RenderTextArea from './RenderTextArea.tsx';
import SearchableDropdown from '../../Common/SearchableDropdown/SearchableDropdown.tsx';
import { getAllParentNodeResults } from '../../helper.tsx';

interface SuggetionObj {
  label: string;
  value: string;
  paramType?: string; // Added paramType for categorization
}

const TextNode = ({ data }: any) => {
  const flowValidationObj = useRecoilValue(flowValidationAtom);
  const [bodyText, setBodyText] = useState(data?.body || '');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [cursorPosition, setCursorPosition] = useState<number | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const flowObj = useRecoilValue(flowObjAtom);

  const parentResults = getAllParentNodeResults(data?.id ?? null, flowObj);

  useEffect(() => {
    if (data.body !== bodyText) {
      setBodyText(data.body || '');
    }
  }, [data.body]);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight + 12}px`;
    }
  }, [bodyText]);

  const handleBodyChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    setBodyText(value);

    const cursorPos = e.target.selectionStart;
    const textBeforeCursor = value.slice(0, cursorPos);

    // Detect `{{@` pattern
    const match = textBeforeCursor.match(/{{@(\w*)$/);
    if (match) {
      setShowSuggestions(true);
      setCursorPosition(cursorPos);
    } else {
      setShowSuggestions(false);
    }

    // Update `params` by finding all valid `{{label}}` entries in the text
    const matches = value.match(/{{(.*?)}}/g); // Match all `{{label}}`
    const labelsInBody = matches ? matches.map((match) => match.replace(/{{|}}/g, '')) : [];

    // Categorize params by `paramType`
    const categorizedParams = {
      global: [],
      result: [],
      webform: [],
    };

    data.parentResults.forEach((result: SuggetionObj) => {
      if (labelsInBody.includes(result.label)) {
        if (result.paramType === 'global') {
          categorizedParams.global.push(result.value);
        } else if (result.paramType === 'result') {
          categorizedParams.result.push(result.value);
        } else if (result.paramType === 'webform') {
          categorizedParams.webform.push(result.value);
        }
      }
    });
    data.onChange(data.id, {
      body: value,
      params: { ...categorizedParams, entity: value ? (data?.params?.entity ?? {}) : {} },
    });
  };

  return (
    <div>
      <Box mb={2}>
        {/* <FormControlLabel
          control={
            <Switch
              checked={data.ai_mode_enabled ?? false}
              onChange={(e) => data.onChange(data.id, { ai_mode_enabled: e.target.checked })}
              color="primary"
            />
          }
          label="AI"
          labelPlacement="end"
        /> */}
        <FormControlLabel
          control={
            <Switch
              checked={data.ai_mode_enabled ?? false}
              onChange={(e) => {
                const enabled = e.target.checked;

                if (!enabled) {
                  data.onChange(data.id, { ai_mode_enabled: false, ai_query_parameters: [] });
                } else {
                  data.onChange(data.id, { ai_mode_enabled: true });
                }
              }}
              color="primary"
            />
          }
          label="AI"
          labelPlacement="end"
        />
        {data.ai_mode_enabled && (
          <SearchableDropdown
            options={parentResults.map((item) => ({
              label: item.label,
              value: item.value,
            }))}
            multiSelect={true}
            onChange={(selectedOptions) => {
              const selectedValues = (selectedOptions as any[]).map((opt) => opt.value);
              data.onChange(data.id, { ai_query_parameters: selectedValues });
            }}
            value={parentResults
              .map((item) => ({ label: item.label, value: item.value }))
              .filter((item) => (data.ai_query_parameters || []).includes(item.value))}
          />
        )}
      </Box>
      <RenderTextArea data={data} />
      {flowValidationObj[data?.id]?.body && (
        <Typography variant="body2" color="error" style={{ fontSize: '12px' }}>
          {flowValidationObj[data?.id]?.body}
        </Typography>
      )}
    </div>
  );
};

export default TextNode;
